import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Loader2, MessageCircleHeart, SendHorizontal, Sparkles, X } from 'lucide-react'

import { supabase } from '@/integrations/supabase/client'
import type { ChatMessage } from '@/integrations/supabase/ai'
import { cn } from '@/lib/utils'

interface DisplayMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
}

const SUGGESTIONS = [
  'Come funziona Make me a Gift?',
  'Chi riceve il link deve registrarsi?',
  'Quanto costa?',
]

const GREETING: DisplayMessage = {
  id: 'greeting',
  role: 'assistant',
  content: 'Ciao! Sono l\u2019assistente di Make me a Gift. Chiedimi pure come funziona, come si condivide la lista o come si scelgono i regali.',
}

function randomId() {
  return `m_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

function renderMarkdown(text: string) {
  const parts: (string | JSX.Element)[] = []
  const regex = /\*\*(.+?)\*\*/g
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index))
    parts.push(<strong key={key++}>{match[1]}</strong>)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return parts
}

async function* streamPublicChat(messages: ChatMessage[]): AsyncGenerator<string> {
  const { data: sessionData } = await supabase.auth.getSession()
  const token = sessionData.session?.access_token
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-public-chat`

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ messages, stream: true }),
  })

  if (!resp.ok || !resp.body) {
    const detail = await resp.json().catch(() => null)
    throw new Error(detail?.error ?? 'Errore di connessione')
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed.startsWith('data:')) continue
      const payload = trimmed.slice(5).trim()
      if (payload === '[DONE]') return
      try {
        const delta = JSON.parse(payload)?.choices?.[0]?.delta?.content
        if (delta) yield delta as string
      } catch {
        // keep-alive / non-JSON line
      }
    }
  }
}

export default function PublicChatWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<DisplayMessage[]>([GREETING])
  const [input, setInput] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, open])

  async function send(text: string) {
    const trimmed = text.trim()
    if (!trimmed || isRunning) return
    setInput('')
    setError(null)

    const userMsg: DisplayMessage = { id: randomId(), role: 'user', content: trimmed }
    const assistantId = randomId()
    setMessages((prev) => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '' }])
    setIsRunning(true)

    const history: ChatMessage[] = [...messages.filter((m) => m.id !== 'greeting' || m.role === 'assistant'), userMsg]
      .filter((m) => m.content.length > 0)
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }))

    try {
      let acc = ''
      for await (const delta of streamPublicChat(history)) {
        acc += delta
        const partial = acc
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: partial } : m)),
        )
      }
      if (!acc) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: 'Non sono riuscita a rispondere. Riprova!' }
              : m,
          ),
        )
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Qualcosa è andato storto'
      setMessages((prev) => prev.filter((m) => m.id !== assistantId || m.content.length > 0))
      setError(msg)
    } finally {
      setIsRunning(false)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    void send(input)
  }

  const showSuggestions = messages.length === 1 && !isRunning

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="w-[calc(100vw-2.5rem)] max-w-sm h-[480px] max-h-[70vh] glass-strong rounded-lg shadow-lg shadow-neutral-900/10 border border-border flex flex-col overflow-hidden"
          >
            <header className="px-4 py-3 flex items-center gap-3 bg-neutral-900 text-white">
                <span className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm leading-tight">Assistente Make me a Gift</p>
                <p className="text-xs text-white/80 flex items-center gap-1.5">
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full',
                      isRunning ? 'bg-emerald-300 animate-pulse' : 'bg-white/60',
                    )}
                  />
                  {isRunning ? 'Sta scrivendo…' : 'Online'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Chiudi chat"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background">
              {messages.map((m) => (
                <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap',
                      m.role === 'user'
                        ? 'bg-neutral-900 text-white rounded-br-md'
                        : 'bg-white border border-border text-neutral-700 rounded-bl-md shadow-sm',
                    )}
                  >
                    {m.content ? renderMarkdown(m.content) : (
                      <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                    )}
                  </div>
                </div>
              ))}

              {showSuggestions && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void send(s)}
                      className="text-xs font-semibold bg-white border border-neutral-300 text-neutral-900 px-3 py-1.5 rounded-full hover:bg-neutral-100 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                  {error}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="border-t border-border p-3 flex gap-2 bg-white/70">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio…"
                className="flex-1 min-w-0 rounded-full border border-input bg-white px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-900/20 transition-shadow"
              />
              <button
                type="submit"
                disabled={!input.trim() || isRunning}
                className="shrink-0 w-10 h-10 bg-neutral-900 text-white rounded-full hover:bg-neutral-800 transition-colors flex items-center justify-center disabled:opacity-50"
                aria-label="Invia messaggio"
              >
                {isRunning ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <SendHorizontal className="w-4 h-4" />
                )}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        whileTap={{ scale: 0.94 }}
        className="hidden w-14 h-14 bg-neutral-900 text-white rounded-full hover:bg-neutral-800 transition-colors shadow-xl shadow-neutral-900/20 items-center justify-center"
        aria-label={open ? 'Chiudi la chat' : 'Apri la chat'}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircleHeart className="w-6 h-6" />}
      </motion.button>
    </div>
  )
}
